import { IEquipmentAttributeTypes } from 'types/graphqlTypes'
import * as yup from 'yup'
import {
  IDoorTypes,
  IReeferDownloadableReportTypes,
  IReeferFlowTypes,
  IReeferTempControlTypes,
  IRoofTypes,
} from 'apps/marketplace/types/equipment'
import { MORE_THAN_ZERO_MESSAGE, REQUIRED_MESSAGE } from '../../constants'

const TEMP_CONTROL_VALUES = Object.values(IReeferTempControlTypes)
const ROOF_OPTION_VALUES = Object.values(IRoofTypes)
const DOOR_OPTION_VALUES = Object.values(IDoorTypes)
const FLOW_OPTION_VALUES = Object.values(IReeferFlowTypes)
const REPORT_OPTION_VALUES = Object.values(IReeferDownloadableReportTypes)

const MIN_MESSAGE = 'Min temperature should be less than max temperature'
const MAX_MESSAGE = 'Max temperature should be more than min temperature'

export const ReeferSchemaObject = {
  /**
   * @example 'Fixed'
   */
  [IEquipmentAttributeTypes.temp_control_type]: yup
    .mixed<IReeferTempControlTypes>()
    .oneOf(TEMP_CONTROL_VALUES, REQUIRED_MESSAGE)
    .nullable()
    .required(REQUIRED_MESSAGE),
  /**
   * @example '40'
   */
  [IEquipmentAttributeTypes.precool]: yup
    .number()
    .nullable()
    .typeError(REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  /**
   * @example '32'
   */
  [IEquipmentAttributeTypes.fixed_temp_control]: yup
    .number()
    .nullable()
    .when(`${IEquipmentAttributeTypes.temp_control_type}`, {
      is: IReeferTempControlTypes.fixed,
      then: yup.number().typeError(REQUIRED_MESSAGE).required(REQUIRED_MESSAGE),
    }),
  /**
   * @example '30'
   */
  [IEquipmentAttributeTypes.min_temp_control]: yup
    .number()
    .nullable()
    .when(`${IEquipmentAttributeTypes.temp_control_type}`, {
      is: IReeferTempControlTypes.range,
      then: yup
        .number()
        .lessThan(yup.ref(`${IEquipmentAttributeTypes.max_temp_control}`), MIN_MESSAGE)
        .typeError(REQUIRED_MESSAGE)
        .required(REQUIRED_MESSAGE),
    }),
  /**
   * @example '35'
   */
  [IEquipmentAttributeTypes.max_temp_control]: yup
    .number()
    .nullable()
    .when(`${IEquipmentAttributeTypes.temp_control_type}`, {
      is: IReeferTempControlTypes.range,
      then: yup
        .number()
        .moreThan(yup.ref(`${IEquipmentAttributeTypes.min_temp_control}`), MAX_MESSAGE)
        .typeError(REQUIRED_MESSAGE)
        .required(REQUIRED_MESSAGE),
    }),
  /**
   * @exmaple 'Start/Stop'
   */
  [IEquipmentAttributeTypes.flow]: yup
    .string()
    .oneOf([...FLOW_OPTION_VALUES, null])
    .nullable()
    .notRequired(),
  /**
   * @example 'Yes'
   */
  [IEquipmentAttributeTypes.downloadable_report]: yup
    .string()
    .oneOf([...REPORT_OPTION_VALUES, null])
    .nullable()
    .notRequired(),
  /**
   * @example false
   */
  [IEquipmentAttributeTypes.load_bars]: yup.boolean().default(false).notRequired(),
  /**
   * @example false
   */
  [IEquipmentAttributeTypes.load_locks]: yup.boolean().default(false).notRequired(),
  /**
   * @example 16
   */
  [IEquipmentAttributeTypes.straps]: yup.number().moreThan(0, MORE_THAN_ZERO_MESSAGE).notRequired(),
  /**
   * @example '2017'
   */
  [IEquipmentAttributeTypes.not_older_than]: yup.string().notRequired(),
  /**
   * @example false
   */
  [IEquipmentAttributeTypes.food_grade]: yup.boolean().default(false).notRequired(),
  /**
   * @example false
   */
  [IEquipmentAttributeTypes.pallet_jack]: yup.boolean().default(false).notRequired(),
  /**
   * @example false
   */
  [IEquipmentAttributeTypes.lift_gate]: yup.boolean().default(false).notRequired(),
  /**
   * @example 'Metal Roof'
   */
  [IEquipmentAttributeTypes.roof_type]: yup
    .string()
    .oneOf([...ROOF_OPTION_VALUES, ''])
    .nullable()
    .notRequired(),
  /**
   * @example 'Swing Doors'
   */
  [IEquipmentAttributeTypes.door_type]: yup
    .string()
    .oneOf([...DOOR_OPTION_VALUES, ''])
    .nullable()
    .notRequired(),
}

export const ReeferSchema = yup.object(ReeferSchemaObject)

export type TReeferSchema = yup.InferType<typeof ReeferSchema>
